import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Eye, Filter, Search } from "lucide-react";
import { apiGet } from "../../processes/helpers/api";
import { CompactAdvancedDropdown, CompactSingleSelectDropdown } from "./TeacherMocksShared";
import QuestionPreviewModal from "./QuestionPreviewModal";
import {
  difficultyLabels,
  difficultyOptions,
  questionDefaultFilters,
  recentFilterOptions,
  typeTagFilterOptions,
} from "./teacherMocksConstants";
import {
  questionFiltersToQuery,
  managerQuery,
  previewText,
  getFirstStem,
  questionTypeTag,
  selectedTypeTagFilters,
} from "./teacherMocksHelpers";

export default function TeacherMocksInlineQuestionBank({
  courseId,
  selectedQuestions,
  onAddQuestion,
  onRemoveQuestion,
  onAddQuestions,
  onClearQuestions,
}) {
  const [filters, setFilters] = useState(questionDefaultFilters(courseId));
  const [appliedFilters, setAppliedFilters] = useState(questionDefaultFilters(courseId));
  const [levels, setLevels] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [previewQuestionId, setPreviewQuestionId] = useState(null);
  const selectedIds = useMemo(
    () => new Set(selectedQuestions.map((question) => Number(question.questionId))),
    [selectedQuestions]
  );
  const moduleLevels = useMemo(
    () => levels.filter((level) => !level.sublevelId),
    [levels]
  );
  const selectedModuleId = useMemo(() => {
    const firstLevelId = filters.fromLevelsExpanded[0];
    return moduleLevels.some((level) => Number(level.id) === Number(firstLevelId)) ? firstLevelId : "";
  }, [filters.fromLevelsExpanded, moduleLevels]);
  const submoduleLevels = useMemo(() => {
    const submodules = levels.filter((level) => !!level.sublevelId);
    if (!selectedModuleId) return submodules;

    return submodules.filter((level) => Number(level.sublevelId) === Number(selectedModuleId));
  }, [levels, selectedModuleId]);
  const selectedSubmoduleId = useMemo(() => {
    const [, secondLevelId] = filters.fromLevelsExpanded;
    if (secondLevelId) return secondLevelId;

    const firstLevelId = filters.fromLevelsExpanded[0];
    return submoduleLevels.some((level) => Number(level.id) === Number(firstLevelId)) ? firstLevelId : "";
  }, [filters.fromLevelsExpanded, submoduleLevels]);

  useEffect(() => {
    setFilters(questionDefaultFilters(courseId));
    setAppliedFilters(questionDefaultFilters(courseId));
    setQuestions([]);
    setLevels([]);
  }, [courseId]);

  useEffect(() => {
    const timeout = setTimeout(() => setAppliedFilters(filters), 350);
    return () => clearTimeout(timeout);
  }, [filters]);

  useEffect(() => {
    let isMounted = true;

    async function loadQuestions() {
      try {
        setIsLoading(true);
        const response = await apiGet(`/api/v2/manager/questions?${questionFiltersToQuery(appliedFilters, 1)}`);
        if (!isMounted) return;
        setQuestions(response.body?.data || []);
        setPagination(response.body?.pagination || null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadQuestions();

    return () => {
      isMounted = false;
    };
  }, [appliedFilters]);

  useEffect(() => {
    if (!courseId) return undefined;
    let isMounted = true;

    async function loadLevels() {
      const query = managerQuery({
        "q[course_id_eq]": courseId,
        "q[s]": "position asc",
        paginated: false,
      });
      const response = await apiGet(`/api/v2/manager/levels?${query}`);
      if (isMounted) setLevels(response.body?.data || []);
    }

    loadLevels();

    return () => {
      isMounted = false;
    };
  }, [courseId]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateTypeTagFilter = (values) => {
    setFilters((current) => ({ ...current, typeTagsIn: values }));
  };

  const handleSearch = (value) => {
    setFilters((current) => ({
      ...current,
      idEq: /^\d+$/.test(value.trim()) ? value.trim() : "",
      contextOrTagsNameCont: /^\d+$/.test(value.trim()) ? "" : value,
    }));
  };

  const selectedTypeTags = selectedTypeTagFilters(filters);

  const toggleQuestion = (question) => {
    const questionId = Number(question.id);
    if (selectedIds.has(questionId)) {
      onRemoveQuestion(questionId);
      return;
    }
    onAddQuestion(question);
  };

  const visibleAvailableQuestions = questions.filter((question) => !selectedIds.has(Number(question.id)));

  const selectAllVisible = () => {
    if (!visibleAvailableQuestions.length) return;
    onAddQuestions(visibleAvailableQuestions);
  };

  const loadMore = async () => {
    if (!pagination?.hasNext || isLoading) return;
    try {
      setIsLoading(true);
      const nextPage = (pagination.page || 1) + 1;
      const response = await apiGet(`/api/v2/manager/questions?${questionFiltersToQuery(appliedFilters, nextPage)}`);
      const newQuestions = response.body?.data || [];
      setQuestions((current) => {
        const ids = new Set(current.map((question) => Number(question.id)));
        return [...current, ...newQuestions.filter((question) => !ids.has(Number(question.id)))];
      });
      setPagination(response.body?.pagination || null);
    } finally {
      setIsLoading(false);
    }
  };

  const searchValue = filters.idEq || filters.contextOrTagsNameCont || "";

  return (
    <>
    <div className="teacher-mocks-create-bank">
      <label className="teacher-mocks-create-search">
        <Search size={16} aria-hidden="true" />
        <input
          value={searchValue}
          onChange={(event) => handleSearch(event.target.value)}
          placeholder="Buscar por ID, texto ou tags da questão..."
        />
      </label>

      <div className="teacher-mocks-create-filter-grid">
        <CompactSingleSelectDropdown
          label="Módulo"
          value={selectedModuleId ? String(selectedModuleId) : ""}
          onChange={(val) => updateFilter("fromLevelsExpanded", val ? [Number(val)] : [])}
          options={moduleLevels.map((level) => ({ value: String(level.id), label: level.name }))}
          placeholder="Todos os módulos"
        />
        <CompactSingleSelectDropdown
          label="Submódulo"
          value={selectedSubmoduleId ? String(selectedSubmoduleId) : ""}
          onChange={(val) => {
            const moduleId = selectedModuleId;
            updateFilter("fromLevelsExpanded", [
              ...(moduleId ? [Number(moduleId)] : []),
              ...(val ? [Number(val)] : []),
            ]);
          }}
          options={submoduleLevels.map((level) => ({ value: String(level.id), label: level.name }))}
          placeholder="Todos"
        />
        <CompactSingleSelectDropdown
          label="Período"
          value={filters.recentFilter}
          onChange={(value) => updateFilter("recentFilter", value)}
          options={recentFilterOptions}
          placeholder="Qualquer"
        />
      </div>

      <button
        type="button"
        className={`teacher-mocks-create-advanced-btn${showAdvancedFilters ? " active" : ""}`}
        onClick={() => setShowAdvancedFilters((current) => !current)}
      >
        <Filter size={15} aria-hidden="true" />
        Avançados
        <ChevronDown size={13} aria-hidden="true" />
      </button>

      {showAdvancedFilters && (
        <div className="teacher-mocks-create-advanced-grid">
          <CompactAdvancedDropdown
            label="Dificuldade"
            options={difficultyOptions}
            selectedValues={filters.difficultyLevelIn}
            onChange={(value) => updateFilter("difficultyLevelIn", value)}
          />
          <CompactAdvancedDropdown
            label="Tags"
            options={typeTagFilterOptions}
            selectedValues={selectedTypeTags}
            onChange={(values) => updateTypeTagFilter(values)}
          />
        </div>
      )}

      <div className="teacher-mocks-create-bank-bottom">
        <div className="teacher-mocks-create-bank-summary">
          <span>
            {pagination?.totalItems != null
              ? `${pagination.totalItems} questões encontradas`
              : isLoading ? "Buscando..." : "0 questões encontradas"}
          </span>
          <div>
            <button type="button" onClick={selectAllVisible} disabled={!visibleAvailableQuestions.length}>
              Selecionar todas
            </button>
            <button type="button" onClick={onClearQuestions} disabled={!selectedQuestions.length}>
              Limpar ({selectedQuestions.length})
            </button>
          </div>
        </div>

        <div className="teacher-mocks-create-bank-list">
        {questions.map((question) => {
          const selected = selectedIds.has(Number(question.id));
          const questionText = previewText(question.context || getFirstStem(question));
          const typeTag = questionTypeTag(question);
          const isInteractive = typeTag.className === "type-interactive";
          const handleQuestionKeyDown = (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleQuestion(question);
            }
          };

          return (
            <div
              key={question.id}
              role="button"
              tabIndex={0}
              className={`teacher-mocks-create-question-row${selected ? " selected" : ""}`}
              onClick={() => toggleQuestion(question)}
              onKeyDown={handleQuestionKeyDown}
            >
              <span className="teacher-mocks-create-question-check">{selected && <Check size={12} aria-hidden="true" />}</span>
              <span className="teacher-mocks-create-question-code">Q-{String(question.id).padStart(4, "0")}</span>
              <div className="teacher-mocks-create-question-body">
                <p className="teacher-mocks-create-question-text">{questionText}</p>
              </div>
              <span className="teacher-mocks-create-question-tags-inline">
                <span className={`teacher-mocks-create-question-pill ${typeTag.className}`}>
                  {typeTag.label}
                </span>
                {question.difficultyLevel && (
                  <span className={`teacher-mocks-create-question-pill difficulty-${question.difficultyLevel}`}>
                    {difficultyLabels[question.difficultyLevel] || question.difficultyLevel}
                  </span>
                )}
              </span>
              {!isInteractive && (
                <button
                  type="button"
                  className="teacher-mocks-create-eye"
                  onClick={(event) => {
                    event.stopPropagation();
                    setPreviewQuestionId(question.id);
                  }}
                  aria-label="Visualizar questão completa"
                >
                  <Eye size={13} color="#64748b" aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
        {isLoading && <div className="teacher-mocks-create-bank-loading">Carregando...</div>}
        {!isLoading && questions.length === 0 && (
          <div className="teacher-mocks-create-bank-loading">Nenhuma questão encontrada.</div>
        )}
        {pagination?.hasNext && !isLoading && (
          <button type="button" className="teacher-mocks-load-more" onClick={loadMore}>
            Carregar mais
          </button>
        )}
        </div>
      </div>
    </div>

    {previewQuestionId && (
      <QuestionPreviewModal
        questionId={previewQuestionId}
        onClose={() => setPreviewQuestionId(null)}
      />
    )}
    </>
  );
}
